import React, { useState, useCallback } from 'react';
import { EmptyState } from '../../../components/common';
import { useAppStore } from '../../../store/useAppStore';
import { useProductionPhase } from '../logic/useProductionPhase';
import { useDashboardAccessibleToolIds } from '../logic/useDashboardAccessibleToolIds';
import { getVisibleWidgets } from '../dashboard-config';
import type { PermissionValue } from '../../auth/types';
import { DashboardWidgetId } from '../types';

// Widget imports
import { MyCallWidget } from '../widgets/MyCallWidget';
import { AnnouncementsWidget } from '../widgets/AnnouncementsWidget';
import { DailyScheduleWidget } from '../widgets/DailyScheduleWidget';
import { AttendanceWidget } from '../widgets/AttendanceWidget';
import { WelcomeCardWidget, useShowWelcomeCard } from '../widgets/WelcomeCardWidget';
import { ActivitySummaryWidget } from '../widgets/ActivitySummaryWidget';
import { ProductionFeedWidget } from '../widgets/ProductionFeedWidget';
import { QuickActionsWidget } from '../widgets/QuickActionsWidget';
import { ActionItemsWidget } from '../widgets/ActionItemsWidget';
import { RehearsalProgressWidget } from '../widgets/RehearsalProgressWidget';
import { LineStudyWidget } from '../widgets/LineStudyWidget';
import { MyPropsChecklistWidget } from '../widgets/MyPropsChecklistWidget';
import { GettingStartedWizard, useShowGettingStartedWizard } from '../../onboarding/components';

interface DashboardPageProps {
    userPermissions?: PermissionValue[];
}

export const DashboardPage: React.FC<DashboardPageProps> = ({ userPermissions = [] }) => {
    const activeWorkspace = useAppStore(state => state.ui.activeWorkspace);
    const phase = useProductionPhase();
    const accessibleToolIds = useDashboardAccessibleToolIds();

    // SM Getting Started Wizard
    const showWizard = useShowGettingStartedWizard();
    const [wizardDismissed, setWizardDismissed] = useState(false);
    const handleWizardDismiss = useCallback(() => setWizardDismissed(true), []);

    // Collaborator Welcome Card
    const showWelcome = useShowWelcomeCard();
    const [welcomeDismissed, setWelcomeDismissed] = useState(false);
    const handleWelcomeDismiss = useCallback(() => setWelcomeDismissed(true), []);

    // Get visible widgets for current workspace and phase
    const visibleWidgets = getVisibleWidgets(activeWorkspace, phase, userPermissions, accessibleToolIds);

    // Map widget IDs to components
    const widgetComponents: Record<DashboardWidgetId, React.ReactNode> = {
        'my-call': <MyCallWidget />,
        'line-study': <LineStudyWidget />,
        'my-props-checklist': <MyPropsChecklistWidget />,
        'daily-schedule': <DailyScheduleWidget />,
        'action-items': <ActionItemsWidget />,
        'quick-actions': <QuickActionsWidget />,
        'rehearsal-progress': <RehearsalProgressWidget />,
        'production-feed': <ProductionFeedWidget />,
        'announcements': <AnnouncementsWidget />,
        'attendance': <AttendanceWidget />,
        'activity-summary': <ActivitySummaryWidget />,
    };

    if (visibleWidgets.length === 0) {
        return (
            <div className="flex items-center justify-center h-full">
                <EmptyState
                    icon="dashboard"
                    title="No Dashboard Available"
                    description="Select a workspace from the sidebar to view your personalized dashboard."
                />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            {/* SM Getting Started Wizard */}
            <GettingStartedWizard
                isOpen={showWizard ? !wizardDismissed : false}
                onDismiss={handleWizardDismiss}
            />

            {/* Phase Indicator */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Production Phase: <span className="font-medium capitalize">{phase.replace('-', ' ')}</span>
                    </p>
                </div>
            </div>

            {/* Collaborator Welcome Card */}
            {showWelcome && !welcomeDismissed && !showWizard ? <WelcomeCardWidget onDismiss={handleWelcomeDismiss} /> : null}

            {/* Widget Grid */}
            <div className="@container">
            <div className="grid grid-cols-1 @md:grid-cols-2 @lg:grid-cols-4 gap-6">
                {visibleWidgets.map(widget => (
                    <div
                        key={widget.id}
                        className={`${
                            widget.span === 4 ? '@md:col-span-2 @lg:col-span-4' :
                            widget.span === 3 ? '@md:col-span-2 @lg:col-span-3' :
                            widget.span === 2 ? '@md:col-span-1 @lg:col-span-2' :
                            '@md:col-span-1 @lg:col-span-1'
                        }`}
                    >
                        {widgetComponents[widget.id]}
                    </div>
                ))}
            </div>
            </div>
        </div>
    );
};
